import type { JSX } from "react";
import type { ExtraProps } from "react-markdown";

import { cn } from "@alaffia-technology-solutions/tailwind-utils";

import URLParse from "url-parse";
import { z } from "zod";

const filePreviewDeepLinkSchema = z.object({
  host: z.literal("file-preview"),
  paths: z.tuple([z.string().min(1)]),
});

// Append here new deep link schemas (use zod discriminatedUnion or union)
const deepLinkSchema = filePreviewDeepLinkSchema;

export type DeepLinkDataShape = Partial<Record<string, unknown>>;

const parseDeepLink = (href: string) => {
  try {
    // url-parse library here is used because it is browser agnostic.
    // The new URL() constructor provided by the browser is handled differently in different browsers.
    // Firefox does not support unknown protocols/schemes (like alaffia://).
    // https://developer.mozilla.org/en-US/docs/Web/API/URL/URL#browser_compatibility
    const url = new URLParse(href);
    const { host, pathname } = url;
    const paths = pathname.split("/").filter(Boolean);

    const parsedDeepLink = deepLinkSchema.safeParse({
      host,
      paths,
    });

    if (!parsedDeepLink.success) {
      throw new Error("Invalid deep link");
    }

    return parsedDeepLink.data;

    // Use this instead if therea are multiple deep link mappers
    // const deepLinkMapper = deepLinkMappers[data.host] as DeepLinkMapper<
    //   typeof data.host
    // >;
  } catch (error) {
    console.error(error);

    return undefined;
  }
};

type AComponentProps = JSX.IntrinsicElements["a"] & ExtraProps;

export const Anchor = ({ children, className, ...rest }: AComponentProps) => (
  <a className={cn("af-pt-4 af-text-primary", className)} {...rest}>
    {children}
  </a>
);

type RenderDeepLink = (
  props: AComponentProps,
  data: z.infer<typeof deepLinkSchema>,
) => JSX.Element;

type RenderLink = (props: AComponentProps) => JSX.Element;

export interface DeepLinkProps extends AComponentProps {
  deepLinkData?: DeepLinkDataShape;
}

interface DeepLinkPropsWithRenderProps extends DeepLinkProps {
  renderDeepLink?: RenderDeepLink;
  renderLink?: RenderLink;
}

export const A = ({
  renderDeepLink,
  renderLink,
  ...props
}: DeepLinkPropsWithRenderProps) => {
  const { href } = props;

  // href is sanitized by the `urlTransform` function in the `ReactMarkdown` component
  if (href?.startsWith("alaffia")) {
    const parsedData = parseDeepLink(href);

    if (parsedData) {
      if (renderDeepLink) {
        return renderDeepLink(props, parsedData);
      }

      if (renderLink) {
        return renderLink(props);
      }

      return <Anchor {...props} />;
    }
  }

  if (renderLink) {
    return renderLink(props);
  }

  return <Anchor {...props} />;
};
